import { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Checkbox, DatePicker, Select, Spin, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Stepper } from '@shared/Stepper';
import { setLastIpotekaId } from '@shared/ipotekaTestId';
import { getFormState, setFormState, clearFormState } from '@shared/ipotekaFormState';
import { MoneyInput } from '@shared/MoneyInput';
import { getGuestKey } from '@shared/guestKey';
import {
    fetchBanks,
    fetchProductTypes,
    fetchPropertyTypes,
    searchCities,
} from './api';
import type { CitySuggestion, DictItem, HouseFlags } from './constants';
import { HOUSE_FLAG_FIELDS, ageRangeFor } from './constants';
import { ArrRightIcon, BookmarkIcon } from './icons';

interface Props {
    userId: string;
    csrf: string;
}

function useDebounced<T extends (...a: never[]) => unknown>(fn: T, delay = 250) {
    const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
    return (...args: Parameters<T>) => {
        if (timer) clearTimeout(timer);
        const t = setTimeout(() => fn(...args), delay);
        setTimer(t);
    };
}

export default function MortgageWelcome({ userId: _userId, csrf }: Props) {
    const [products, setProducts] = useState<DictItem[]>([]);
    const [propertyTypes, setPropertyTypes] = useState<DictItem[]>([]);
    const [banks, setBanks] = useState<DictItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Если на welcome возвращаются повторно — подставляем сохранённые ранее значения
    const initialSaved = getFormState();

    const [productCode, setProductCode] = useState<string | undefined>(initialSaved.productCode);
    const [propertyType, setPropertyType] = useState<string | undefined>(initialSaved.propertyType);
    const [bank, setBank] = useState<string | undefined>(initialSaved.bank);
    const [city, setCity] = useState<string>(initialSaved.city ?? '');
    const [selectedCity, setSelectedCity] = useState<CitySuggestion | null>(
        initialSaved.city ? { value: initialSaved.city } : null,
    );
    const [citySuggestions, setCitySuggestions] = useState<CitySuggestion[]>([]);
    const [submitting, setSubmitting] = useState(false);

    // Дата рождения страхователя. Нужна для Inssmart `A426ContractCreate`
    // (тарифы зависят от возраста) и для Pampadu FastCalc. По умолчанию —
    // 30 лет назад; юзер уточнит в checkout. Сохраняется в `ipoteka.insurer_birthdate`.
    const defaultBirth = dayjs().subtract(30, 'year');
    const [birth, setBirth] = useState<Dayjs>(
        initialSaved.birthIso ? dayjs(initialSaved.birthIso) : defaultBirth,
    );
    const [gender, setGender] = useState<'m' | 'f'>(initialSaved.gender ?? 'm');

    // Codex #1 (находка E2E T4): дефолтные ДР/пол показываем в полях для UX,
    // но в payload их класть НЕЛЬЗЯ, пока юзер не подтвердил явно — иначе бэк
    // считает дефолт «введённым», defaults_applied пустой и гейт codex #1 на
    // checkout холостой. Трекаем явный ввод; при возврате на welcome
    // (saved-значения) считаем поля уже подтверждёнными.
    const [birthTouched, setBirthTouched] = useState<boolean>(!!initialSaved.birthIso);
    const [genderTouched, setGenderTouched] = useState<boolean>(!!initialSaved.gender);

    // Остаток кредита (loan_amount). Тарифно-значимое поле — раньше бэк тихо
    // подставлял 3 000 000 ₽ (codex #1: скрытые дефолты запрещены). Юзер обязан
    // ввести своё значение явно, без него на расчёт уйти нельзя.
    // Строка — храним ровно как ввёл юзер (копейки/хвостовой ноль не теряем).
    const [balance, setBalance] = useState<string>(initialSaved.balance ?? '');

    // Дата начала кредитного договора (loan_agreement_begin_date) — НЕ дата начала
    // страхования (та производна и считается позже). Юр-значимое поле
    // (Ipoteka::LEGAL_FIELDS). Codex #1: молчаливый дефолт запрещён, собираем явно
    // на welcome. По умолчанию — сегодня; в payload кладём ТОЛЬКО при явном вводе
    // (как ДР/пол), иначе бэк пометит его в defaults_applied.
    const [startDate, setStartDate] = useState<Dayjs>(
        initialSaved.startIso ? dayjs(initialSaved.startIso) : dayjs(),
    );
    const [startTouched, setStartTouched] = useState<boolean>(!!initialSaved.startIso);

    // House-флаги объекта (Tier C). Уходят в payload A4317 под этими же именами;
    // A4317 принимает их в whitelist. Дефолт false — флажок-чекбокс.
    const [houseFlags, setHouseFlags] = useState<HouseFlags>({
        no_loan_agreement: false,
        no_property_rights: false,
        has_wooden_floors: false,
        has_gas_supply: false,
    });

    useEffect(() => {
        let alive = true;
        Promise.all([fetchProductTypes(), fetchPropertyTypes(), fetchBanks()])
            .then(([prods, propTypes, banksList]) => {
                if (!alive) return;
                setProducts(prods);
                setPropertyTypes(propTypes);
                setBanks(banksList);
                // Если saved-значение есть в загруженных опциях — оставляем его.
                // Иначе фоллбек на первую опцию из API (saved-значение могло быть
                // от старой версии справочника).
                setProductCode((prev) =>
                    prev && prods.some((p) => p.code === prev) ? prev : prods[0]?.code,
                );
                setPropertyType((prev) =>
                    prev && propTypes.some((p) => p.code === prev) ? prev : propTypes[0]?.code,
                );
                // Bank — если saved есть и его нет в banksList, сбрасываем
                setBank((prev) =>
                    prev && banksList.some((b) => b.code === prev) ? prev : undefined,
                );
            })
            .catch((err: Error) => {
                if (!alive) return;
                setLoadError(err.message || 'Не удалось загрузить справочники');
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, []);

    // Сохраняем все 4 поля welcome в localStorage при каждом изменении —
    // чтобы при возврате на /ipoteka из stepper'а значения подтянулись.
    // Сохраняем и labels, чтобы checkout/payment могли показать «Сбербанк», а не "sber".
    useEffect(() => {
        const productLabel = products.find((p) => p.code === productCode)?.name;
        const bankLabel = banks.find((b) => b.code === bank)?.name;
        const propertyLabel = propertyTypes.find((p) => p.code === propertyType)?.name;
        setFormState({
            productCode,
            productLabel,
            bank,
            bankLabel,
            propertyType,
            propertyLabel,
            city: city.trim() || undefined,
            balance: balance || undefined,
            // Codex #1: дату начала пишем только при явном вводе — иначе при
            // возврате на welcome дефолт «сегодня» не должен считаться подтверждённым.
            startIso: startTouched ? startDate.format('YYYY-MM-DD') : undefined,
            // House-флаги в общий стейт → offers (экран «Расчёт») подхватит их из
            // formState.opts, а не покажет пустыми. Ключи = имена бэкенд-полей.
            opts: houseFlags as unknown as Record<string, boolean>,
        });
    }, [productCode, bank, propertyType, city, balance, startTouched, startDate, products, banks, propertyTypes, houseFlags]);

    const debouncedCitySearch = useDebounced(async (q: string) => {
        const suggestions = await searchCities(q);
        setCitySuggestions(suggestions);
    }, 250);

    const cityOptions = useMemo(
        () => citySuggestions.map((s) => ({ value: s.value, label: s.value })),
        [citySuggestions],
    );

    const cityIsValid = !!selectedCity && selectedCity.value === city.trim();
    const cityShowError = city.trim().length >= 2 && !cityIsValid;
    // Остаток кредита обязателен (codex #1). Минимум 20 000 ₽ — нижняя граница,
    // которую раньше прикрывал скрытый дефолт бэка. Деньги — не более 2 знаков
    // после запятой (копейки); 3+ знаков = ошибка, дальше не пускаем.
    const balanceNum = balance.trim() === '' ? null : Number(balance);
    const balanceTooManyDecimals = /\.\d{3,}/.test(balance);
    const balanceIsValid = balanceNum != null && Number.isFinite(balanceNum) && balanceNum >= 20000 && !balanceTooManyDecimals;
    const balanceShowError = (balanceNum != null && Number.isFinite(balanceNum) && balanceNum < 20000) || balanceTooManyDecimals;

    // A2: ранняя клиентская валидация возраста страхователя. Диапазон зависит от
    // типа страхования (для «Жизнь» — своё окно), сами диапазоны в constants.ts.
    // Возраст-подсказка считается на СЕГОДНЯ (≈ начало страхового периода): дата
    // начала кредитного договора может быть в прошлом, считать возраст на неё
    // нельзя (дала бы неактуальный/отрицательный возраст и заблокировала бы шаг).
    // Бэкенд fail-closed — это лишь подсказка, чтобы не упереться в пустую выдачу.
    const ageRange = ageRangeFor(productCode);
    const age = dayjs().diff(birth, 'year');
    const ageIsValid = age >= ageRange.min && age <= ageRange.max;
    const ageShowError = !ageIsValid;
    // Дата начала кредитного договора: от 01.01.1990 до +10 лет от сегодня
    // (может быть в прошлом — кредит уже оформлен).
    const startMin = dayjs('1990-01-01');
    const startMax = dayjs().add(10, 'year');
    const startValid = !!startDate && startDate.isValid()
        && !startDate.isBefore(startMin, 'day')
        && !startDate.isAfter(startMax, 'day');
    const startShowError = !startValid;
    const ready =
        !!productCode && !!propertyType && !!bank && cityIsValid && balanceIsValid && ageIsValid && startValid;

    async function onSubmit() {
        if (!ready || submitting) return;
        setSubmitting(true);

        const payload: Record<string, unknown> = {
            product_code: productCode,
            property_type: propertyType,
            bank_code: bank,
            property_city: city.trim(),
            // Остаток кредита — введён юзером явно (codex #1). Без него `ready`
            // не пускает на расчёт, поэтому здесь balance гарантированно валиден.
            loan_amount: balance,
        };
        // Codex #1: ДР/пол отправляем ТОЛЬКО если юзер ввёл их явно. Если поле
        // не тронуто — ключ не кладём, и бэк (A4317) подставит дефолт и пометит
        // его в defaults_applied → гейт codex #1 сработает на checkout.
        if (birthTouched) {
            payload.insurer_birthdate = birth.format('YYYY-MM-DD');
        }
        if (genderTouched) {
            payload.insurer_gender = gender === 'f' ? 'female' : 'male';
        }
        // Codex #1: дату начала договора шлём только при явном вводе. Имя поля —
        // как в БД/A4323 (Ipoteka::LEGAL_FIELDS): loan_agreement_begin_date.
        // Если не тронута — бэк подставит дефолт и пометит в defaults_applied,
        // гейт механизма B потребует подтверждения на checkout.
        if (startTouched) {
            payload.loan_agreement_begin_date = startDate.format('YYYY-MM-DD');
        }

        // Tier C: house-флаги объекта уходят в A4317 под этими же именами.
        // Всегда шлём явные boolean'ы (дефолт false), чтобы whitelist получил
        // полную картину по объекту.
        payload.no_loan_agreement = houseFlags.no_loan_agreement;
        payload.no_property_rights = houseFlags.no_property_rights;
        payload.has_wooden_floors = houseFlags.has_wooden_floors;
        payload.has_gas_supply = houseFlags.has_gas_supply;

        // Tier C: сырьё dadata по выбранному городу + fias. selectedCity здесь
        // не null (cityIsValid в `ready`). fias — наиболее точный из
        // settlement_fias_id / city_fias_id. Ключ именно `fias` — его читает
        // A4317 (requestData['fias'] → property_city_fias_id). Старое имя
        // property_fias молча терялось.
        //
        // property_city_data — ЦЕЛИКОМ suggestion (с верхнеуровневым value), а не
        // только .data: Addr::run/IpotekaBeforeSave используют кэш лишь когда
        // decoded['value'] === property_city. value гарантированно равен
        // city.trim()===property_city (cityIsValid в `ready`). Иначе бэк счёл бы
        // кэш «устаревшим», переспросил бы DaData и затёр fias на city_fias_id.
        if (selectedCity?.data) {
            payload.property_city_data = selectedCity;
            const fias =
                selectedCity.data.settlement_fias_id || selectedCity.data.city_fias_id;
            if (fias) payload.fias = fias;
        }

        try {
            const res = await fetch('/v1/ipoteka/a4317-ipoteka-create', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrf,
                    'Guest-Key': getGuestKey(),
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error(`HTTP ${res.status}: ${text}`);
            }

            const data = (await res.json()) as { id?: string | number; status?: string };
            if (data.status && data.status !== 'success') {
                throw new Error('API вернул статус: ' + data.status);
            }
            const id = data.id;
            if (!id) throw new Error('Нет id контракта в ответе');

            setLastIpotekaId(id);
            // B-5 (G1): создаём НОВУЮ заявку (A4317 = new Ipoteka). Старый
            // formState мог содержать выбранный оффер/опции/цены/дату от прошлой
            // заявки (selectedOffer, opts, startIso, selectedPrice, coverage...).
            // Полностью очищаем перед записью свежих welcome-полей, чтобы хвосты
            // прошлой сессии не протекли на новый ipoteka_id.
            clearFormState();
            setFormState({
                productCode,
                productLabel: products.find((p) => p.code === productCode)?.name,
                bank,
                bankLabel: banks.find((b) => b.code === bank)?.name,
                propertyType,
                propertyLabel: propertyTypes.find((p) => p.code === propertyType)?.name,
                city: city.trim(),
                balance: balance || undefined,
                // Codex #1: сохраняем ДР/пол/дату начала только при явном вводе,
                // чтобы при возврате на welcome они не считались уже
                // подтверждёнными (*Touched инициализируются из saved).
                birthIso: birthTouched ? birth.format('YYYY-MM-DD') : undefined,
                gender: genderTouched ? gender : undefined,
                startIso: startTouched ? startDate.format('YYYY-MM-DD') : undefined,
                // House-флаги → offers подхватит из formState.opts (ключи = бэкенд-поля).
                opts: houseFlags as unknown as Record<string, boolean>,
            });
            window.location.href = `/ipoteka/offers?id=${encodeURIComponent(String(id))}`;
        } catch (err) {
            console.error(err);
            message.error('Не удалось создать контракт. Попробуйте ещё раз.');
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="page-wrap">
                <div className="loading-block">
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="page-wrap">
                <div className="form-card">
                    <p>Не удалось загрузить справочники: {loadError}</p>
                    <p>Обновите страницу или попробуйте позже.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrap">
            <Stepper active={0} />
            <div className="page-head">
                <h1 className="page-title">Страхование ипотеки</h1>
                <p className="page-sub">
                    Сравните предложения банков-партнёров за 1 минуту.
                </p>
            </div>

            <div className="form-card">
                <div className="grid-2">
                    <div className="field" style={{ gridColumn: '1 / -1' }}>
                        <label>
                            Тип страхования<span className="req">*</span>
                        </label>
                        <Select
                            value={productCode}
                            onChange={setProductCode}
                            placeholder="Выберите тип страхования"
                            options={products.map((p) => ({ value: p.code, label: p.name }))}
                        />
                    </div>

                    <div className="field">
                        <label>Тип недвижимости</label>
                        <Select
                            value={propertyType}
                            onChange={setPropertyType}
                            options={propertyTypes.map((p) => ({ value: p.code, label: p.name }))}
                        />
                    </div>
                    <div className="field">
                        <label>
                            Ипотечный банк<span className="req">*</span>
                        </label>
                        <Select
                            value={bank}
                            onChange={setBank}
                            placeholder="Выберите банк"
                            showSearch
                            options={banks.map((b) => ({ value: b.code, label: b.name }))}
                            filterOption={(i, o) =>
                                (o?.label as string).toLowerCase().includes(i.toLowerCase())
                            }
                        />
                    </div>

                    <div className="field" style={{ gridColumn: '1 / -1' }}>
                        <label>
                            Остаток кредита, ₽<span className="req">*</span>
                        </label>
                        <MoneyInput
                            value={balance}
                            onChange={setBalance}
                            placeholder="Например, 3 000 000"
                            status={balanceShowError ? 'error' : undefined}
                        />
                        {balanceShowError && (
                            <span className="hint hint-error">
                                {balanceTooManyDecimals
                                    ? 'Не более 2 знаков после запятой (копейки)'
                                    : 'Укажите остаток кредита (от 20 000 ₽)'}
                            </span>
                        )}
                    </div>

                    <div className="field">
                        <label>
                            Дата рождения страхователя<span className="req">*</span>
                        </label>
                        <DatePicker
                            value={birth}
                            onChange={(v) => { if (v) { setBirth(v); setBirthTouched(true); } }}
                            format={['DD.MM.YYYY', 'DDMMYYYY', 'DD,MM,YYYY']}
                            allowClear={false}
                            style={{ width: '100%' }}
                            placeholder="дд.мм.гггг"
                            status={ageShowError ? 'error' : undefined}
                        />
                        {ageShowError && (
                            <span className="hint hint-error">
                                Возраст страхователя должен быть от {ageRange.min} до{' '}
                                {ageRange.max} лет на дату начала договора
                            </span>
                        )}
                    </div>
                    <div className="field">
                        <label>Пол</label>
                        <Select
                            value={gender}
                            onChange={(v) => {
                                setGender(v);
                                setGenderTouched(true);
                            }}
                            options={[
                                { value: 'm', label: 'Мужской' },
                                { value: 'f', label: 'Женский' },
                            ]}
                        />
                    </div>

                    {/* Дата начала кредитного договора — юр-значимое поле (codex #1).
                        Обязательна; диапазон 01.01.1990 … +10 лет от сегодня. */}
                    <div className="field">
                        <label>Дата начала кредитного договора<span className="req">*</span></label>
                        <DatePicker
                            value={startDate}
                            onChange={(v) => { if (v) { setStartDate(v); setStartTouched(true); } }}
                            format={['DD.MM.YYYY', 'DDMMYYYY', 'DD,MM,YYYY']}
                            allowClear={false}
                            style={{ width: '100%' }}
                            placeholder="дд.мм.гггг"
                            status={startShowError ? 'error' : undefined}
                            disabledDate={(d) => !!d && (d.isBefore(startMin, 'day') || d.isAfter(startMax, 'day'))}
                        />
                        {startShowError && (
                            <span className="hint hint-error">
                                Дата начала кредитного договора — от 01.01.1990 до {startMax.format('DD.MM.YYYY')}
                            </span>
                        )}
                    </div>

                    <div className="field" style={{ gridColumn: '1 / -1' }}>
                        <label>
                            Город нахождения объекта<span className="req">*</span>
                        </label>
                        <AutoComplete
                            value={city}
                            onChange={(v) => {
                                setCity(v);
                                if (selectedCity && selectedCity.value !== v) {
                                    setSelectedCity(null);
                                }
                                if (v && v.trim().length >= 2) debouncedCitySearch(v);
                            }}
                            onSelect={(v) => {
                                setCity(v);
                                const match = citySuggestions.find((s) => s.value === v);
                                if (match) setSelectedCity(match);
                            }}
                            onClear={() => {
                                setCity('');
                                setSelectedCity(null);
                            }}
                            options={cityOptions}
                            placeholder="Например, Москва"
                            allowClear
                            status={cityShowError ? 'error' : undefined}
                        />
                        {cityShowError && (
                            <span className="hint hint-error">
                                Выберите город из выпадающего списка
                            </span>
                        )}
                    </div>

                    {/* House-флаги объекта (Tier C). Опциональны, уходят в A4317. */}
                    <div className="field" style={{ gridColumn: '1 / -1' }}>
                        <label>Характеристики объекта</label>
                        <div className="house-flags">
                            {HOUSE_FLAG_FIELDS.map(({ key, label }) => (
                                <Checkbox
                                    key={key}
                                    checked={houseFlags[key]}
                                    onChange={(e) =>
                                        setHouseFlags((prev) => ({
                                            ...prev,
                                            [key]: e.target.checked,
                                        }))
                                    }
                                >
                                    {label}
                                </Checkbox>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="form-foot">
                    <button
                        type="button"
                        className="submit-big"
                        disabled={!ready || submitting}
                        onClick={onSubmit}
                    >
                        {submitting ? 'Создаём расчёт…' : 'Перейти к расчёту'}
                        <ArrRightIcon />
                    </button>
                    <a href="/app/contracts?status=draft" className="save-link">
                        <BookmarkIcon /> Мои черновики
                    </a>
                </div>
            </div>
        </div>
    );
}
